import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  togglePinnedFiltersState,
  selectSubscriptionsPinnedFilters,
} from '@store';
import {
  BUNDLE_STATUS_OPTIONS,
  BUNDLE_TYPE_OPTIONS,
  deliveryLocationOptions,
  ERFUDD_RESERVATION_TYPE,
  ERFUDD_STATUS,
  subscriptionStatusOptions,
  typeOptions,
} from 'components/subscriptions/SubscriptionsFilters/constants';
import { omit } from 'lodash-es';
import { BOOKING_LABELS } from '@constants';

export const useSubscriptionsFilterConfig = ({
  enums,
  subscriptionAssigneesOptions,
  assigneesWithNone,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectSubscriptionsPinnedFilters);
  const isErfuddAlert = router.query.isErfuddAlert === 'true';
  const statusOptions = useMemo(
    () => (isErfuddAlert ? [ERFUDD_STATUS] : subscriptionStatusOptions),
    [isErfuddAlert]
  );

  const deliveryStatusOptions = enums?.deliveryStatuses?.map((status) => ({
    text: status?.name,
    value: status?.id,
  }));
  const outOfSlaOptions = useMemo(
    () =>
      enums?.outOfSla?.map((option) => ({
        text: option?.name,
        value: option?.id,
      })) || [],
    [enums?.outOfSla]
  );
  const customerTypeOptions = useMemo(
    () =>
      enums?.customerTypes?.map((option) => ({
        text: option?.name,
        value: option?.id,
      })) || [],
    [enums?.customerTypes]
  );

  // When isErfuddAlert is active, restrict options and force values
  const reservationTypeOptions = useMemo(
    () => (isErfuddAlert ? [ERFUDD_RESERVATION_TYPE] : enums.reservationsTypes),
    [isErfuddAlert, enums.reservationsTypes]
  );

  const filterCategories = useMemo(
    () => [
      {
        id: 'status',
        label: 'Subscription Status',
        icon: RadioButtonUncheckedIcon,
        options: statusOptions,
        multiSelect: true,
      },
      {
        id: 'type',
        label: 'Type',
        icon: RadioButtonUncheckedIcon,
        options: typeOptions,
        multiSelect: true,
      },
      {
        id: 'bundleType',
        label: 'Bundle Type',
        icon: RadioButtonUncheckedIcon,
        options: BUNDLE_TYPE_OPTIONS,
        multiSelect: true,
        delimiter: '|',
      },
      {
        id: 'bundleStatus',
        label: 'Bundle Status',
        icon: RadioButtonUncheckedIcon,
        options: BUNDLE_STATUS_OPTIONS,
        multiSelect: true,
      },
      {
        id: 'deliveryStatus',
        label: 'Delivery Status',
        icon: RadioButtonUncheckedIcon,
        options: deliveryStatusOptions || [],
        multiSelect: true,
      },
      {
        id: 'deliveryLocation',
        label: 'Delivery Location',
        icon: RadioButtonUncheckedIcon,
        options: deliveryLocationOptions || [],
        multiSelect: true,
      },
      {
        id: 'labels',
        label: 'Labels',
        icon: RadioButtonUncheckedIcon,
        options: BOOKING_LABELS || [],
        multiSelect: true,
      },
      {
        id: 'assignee',
        label: 'Assignees',
        icon: RadioButtonUncheckedIcon,
        options: subscriptionAssigneesOptions || [],
        multiSelect: true,
      },
      {
        id: 'procurementStage',
        label: 'Procurement Stage',
        icon: RadioButtonUncheckedIcon,
        options: enums?.procurementStages || [],
        multiSelect: true,
      },
      {
        id: 'reservationType',
        label: 'Reservation Type',
        icon: RadioButtonUncheckedIcon,
        options: reservationTypeOptions || [],
        multiSelect: true,
      },
      {
        id: 'availabilityStatus',
        label: 'Vehicle Availability Status',
        icon: RadioButtonUncheckedIcon,
        options: enums.statuses || [],
        multiSelect: true,
      },
      {
        id: 'bookingAgent',
        label: 'Booking Agent',
        icon: RadioButtonUncheckedIcon,
        options: assigneesWithNone || [],
        multiSelect: true,
      },
      {
        id: 'outOfSla',
        label: 'Out of SLA',
        icon: RadioButtonUncheckedIcon,
        options: outOfSlaOptions || [],
        multiSelect: true,
      },
      {
        id: 'customerTypes',
        label: 'Customer Types',
        icon: RadioButtonUncheckedIcon,
        options: customerTypeOptions || [],
        multiSelect: true,
      },
    ],
    [
      assigneesWithNone,
      deliveryStatusOptions,
      enums?.procurementStages,
      enums.statuses,
      outOfSlaOptions,
      reservationTypeOptions,
      statusOptions,
      subscriptionAssigneesOptions,
      customerTypeOptions,
    ]
  );
  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(
      togglePinnedFiltersState({ board: 'subscriptions', pinnedFilter })
    );
  };

  const hasFilters =
    pinnedFilters?.length > 0 ||
    Object.keys(
      omit(router.query, [
        'page',
        'pageSize',
        'order',
        'orderBy',
        'query',
        'isErfuddAlert',
      ])
    ).length > 0;

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value, delimiter = ',') => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(delimiter).filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      status: parseQueryValue(router.query.status),
      type: parseQueryValue(router.query.type),
      bundleType: parseQueryValue(router.query.bundleType, '|'),
      bundleStatus: parseQueryValue(router.query.bundleStatus),
      deliveryStatus: parseQueryValue(router.query.deliveryStatus),
      deliveryLocation: parseQueryValue(router.query.deliveryLocation),
      labels: parseQueryValue(router.query.labels),
      assignee: parseQueryValue(router.query.assignee),
      procurementStage: parseQueryValue(router.query.procurementStage),
      reservationType: parseQueryValue(router.query.reservationType),
      availabilityStatus: parseQueryValue(router.query.availabilityStatus),
      bookingAgent: parseQueryValue(router.query.bookingAgent),
      outOfSla: parseQueryValue(router.query.outOfSla),
      customerTypes: parseQueryValue(router.query.customerTypes),
    };
  }, [router.query]);

  return {
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
  };
};
